import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * `below-end` hangs under the trigger with right edges aligned (header
 * controls at the right of the bar). `above-*` opens upward from a trigger
 * at the bottom of the screen (the sidebar's account card).
 */
export type PopoverPlacement = 'below-end' | 'above-end' | 'above-start';

export type PopoverPosition = { top?: number; bottom?: number; left: number; width: number };

type TriggerRect = { left: number; right: number; top: number; bottom: number };

const GAP = 10;
const EDGE = 16;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Where a fixed popover goes for a trigger, never closer than 16px to the
 * window's sides. Given the popover's height, it opens on the other side
 * of the trigger when the preferred one is too short and the other roomier.
 */
export function popoverPosition(
    rect: TriggerRect,
    viewport: { width: number; height: number },
    preferredWidth: number,
    placement: PopoverPlacement,
    height = 0,
): PopoverPosition {
    const width = Math.min(preferredWidth, viewport.width - EDGE * 2);
    const maxLeft = viewport.width - width - EDGE;
    const left = clamp(placement === 'above-start' ? rect.left : rect.right - width, EDGE, maxLeft);

    const roomAbove = rect.top - GAP - EDGE;
    const roomBelow = viewport.height - rect.bottom - GAP - EDGE;
    const openAbove = placement === 'below-end'
        ? height > roomBelow && roomAbove > roomBelow
        : height <= roomAbove || roomAbove >= roomBelow;

    return openAbove
        ? { bottom: viewport.height - rect.top + GAP, left, width }
        : { top: rect.bottom + GAP, left, width };
}

const samePosition = (a: PopoverPosition | null, b: PopoverPosition) =>
    !!a && a.top === b.top && a.bottom === b.bottom && a.left === b.left && a.width === b.width;

/**
 * Open state, placement and dismissal for a popover portalled to <body>.
 * Portalled because the header gains a backdrop-filter once scrolled,
 * which would otherwise become the containing block of a fixed popover,
 * and the sidebar clips anything that overflows it.
 */
export function usePopover<T extends HTMLElement = HTMLButtonElement>({
    width,
    placement = 'below-end',
}: {
    width: number;
    placement?: PopoverPlacement;
}) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<PopoverPosition | null>(null);
    const triggerRef = useRef<T | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    const place = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const next = popoverPosition(rect, viewport, width, placement, popoverRef.current?.offsetHeight ?? 0);
        // Same object back when nothing moved, so re-placing can't loop.
        setPosition((prev) => (samePosition(prev, next) ? prev : next));
    }, [width, placement]);

    const close = useCallback((returnFocus = true) => {
        setOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    }, []);

    const toggle = useCallback(() => setOpen((prev) => !prev), []);

    // Before paint, so the popover never flashes where it last was.
    useLayoutEffect(() => {
        if (open) place();
    }, [open, place]);

    // Its height is only known once it's on screen, and changes when the
    // content does (the account menu swapping to the location view) — each
    // time, it may need the other side of the trigger.
    const shown = open && position !== null;
    useLayoutEffect(() => {
        const element = popoverRef.current;
        if (!shown || !element) return;
        place();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => place());
        observer.observe(element);
        return () => observer.disconnect();
    }, [shown, place]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
            close(false);
        };
        window.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, close, place]);

    return { open: shown, toggle, close, triggerRef, popoverRef, position };
}
